import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CreditCard, Landmark, ShieldCheck, Truck, MessageSquare, Mail,
  Plug, Settings, Activity, Key, Copy, Eye, EyeOff, RefreshCw,
  CheckCircle2, XCircle, Clock, ExternalLink, Code, Zap, Globe
} from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────

type IntegrationStatus = "connected" | "disconnected" | "error" | "pending";
type IntegrationCategory = "payment" | "bank" | "gst" | "eway" | "sms" | "email";

interface Integration {
  id: string;
  name: string;
  description: string;
  category: IntegrationCategory;
  icon: React.ReactNode;
  status: IntegrationStatus;
  lastSync?: string;
  version?: string;
  apiKeyMasked?: string;
  webhookUrl?: string;
  environment: "sandbox" | "production";
}

interface APIEndpoint {
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  auth: string;
}

// ── Seed data ──────────────────────────────────────────────────────────

const INITIAL_INTEGRATIONS: Integration[] = [
  { id: "razorpay", name: "Razorpay", description: "Accept payments via UPI, cards, netbanking, wallets", category: "payment", icon: <CreditCard className="h-5 w-5" />, status: "connected", lastSync: "2026-03-08T10:30:00Z", version: "v2", apiKeyMasked: "rzp_live_****x9Kf", environment: "production" },
  { id: "paytm", name: "Paytm Business", description: "Payment gateway for online & offline payments", category: "payment", icon: <CreditCard className="h-5 w-5" />, status: "disconnected", environment: "sandbox" },
  { id: "stripe", name: "Stripe", description: "Global payment processing for international clients", category: "payment", icon: <CreditCard className="h-5 w-5" />, status: "disconnected", environment: "sandbox" },
  { id: "icici", name: "ICICI Bank API", description: "Corporate banking API for account statements & payments", category: "bank", icon: <Landmark className="h-5 w-5" />, status: "connected", lastSync: "2026-03-08T06:00:00Z", version: "v1", apiKeyMasked: "icici_****a7Bx", environment: "production" },
  { id: "hdfc", name: "HDFC Bank API", description: "Business banking integration for reconciliation", category: "bank", icon: <Landmark className="h-5 w-5" />, status: "pending", environment: "sandbox" },
  { id: "sbi", name: "SBI Connect", description: "State Bank of India corporate banking API", category: "bank", icon: <Landmark className="h-5 w-5" />, status: "disconnected", environment: "sandbox" },
  { id: "gst-portal", name: "GST Portal (NIC)", description: "Official GST filing, return submission & verification", category: "gst", icon: <ShieldCheck className="h-5 w-5" />, status: "connected", lastSync: "2026-03-07T18:00:00Z", version: "v3.0", apiKeyMasked: "gst_****pQ2m", environment: "production" },
  { id: "cleartax", name: "ClearTax GST", description: "Automated GST reconciliation & filing", category: "gst", icon: <ShieldCheck className="h-5 w-5" />, status: "disconnected", environment: "sandbox" },
  { id: "eway-nic", name: "E-Way Bill Portal (NIC)", description: "Official E-Way bill generation & management API", category: "eway", icon: <Truck className="h-5 w-5" />, status: "connected", lastSync: "2026-03-08T08:15:00Z", version: "v1.03", apiKeyMasked: "eway_****rT5x", environment: "production" },
  { id: "msg91", name: "MSG91", description: "Transactional SMS for OTPs, alerts & notifications", category: "sms", icon: <MessageSquare className="h-5 w-5" />, status: "connected", lastSync: "2026-03-08T11:00:00Z", apiKeyMasked: "msg91_****kL3n", environment: "production" },
  { id: "twilio", name: "Twilio", description: "Global SMS, WhatsApp & voice communication", category: "sms", icon: <MessageSquare className="h-5 w-5" />, status: "disconnected", environment: "sandbox" },
  { id: "sendgrid", name: "SendGrid", description: "Transactional & marketing email delivery", category: "email", icon: <Mail className="h-5 w-5" />, status: "connected", lastSync: "2026-03-08T09:45:00Z", apiKeyMasked: "SG.****vN8q", environment: "production" },
  { id: "mailgun", name: "Mailgun", description: "Email API for invoices, reports & notifications", category: "email", icon: <Mail className="h-5 w-5" />, status: "disconnected", environment: "sandbox" },
];

const REST_API_ENDPOINTS: APIEndpoint[] = [
  { method: "GET", path: "/api/v1/ledgers", description: "List all ledgers with balances", auth: "Bearer Token" },
  { method: "POST", path: "/api/v1/vouchers", description: "Create a new voucher entry", auth: "Bearer Token" },
  { method: "GET", path: "/api/v1/vouchers/{id}", description: "Get voucher details by ID", auth: "Bearer Token" },
  { method: "GET", path: "/api/v1/invoices", description: "List all invoices with filters", auth: "Bearer Token" },
  { method: "POST", path: "/api/v1/invoices", description: "Create a new GST invoice", auth: "Bearer Token" },
  { method: "GET", path: "/api/v1/gst/returns", description: "Fetch GST return summary", auth: "Bearer Token" },
  { method: "POST", path: "/api/v1/eway-bills", description: "Generate a new E-Way bill", auth: "Bearer Token" },
  { method: "GET", path: "/api/v1/reports/trial-balance", description: "Get trial balance report", auth: "Bearer Token" },
  { method: "GET", path: "/api/v1/reports/profit-loss", description: "Get profit & loss statement", auth: "Bearer Token" },
  { method: "GET", path: "/api/v1/reports/balance-sheet", description: "Get balance sheet report", auth: "Bearer Token" },
  { method: "POST", path: "/api/v1/webhooks", description: "Register a webhook endpoint", auth: "API Key" },
  { method: "DELETE", path: "/api/v1/webhooks/{id}", description: "Remove a webhook endpoint", auth: "API Key" },
];

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  payment: "Payment Gateways",
  bank: "Bank APIs",
  gst: "GST Portals",
  eway: "E-Way Bill APIs",
  sms: "SMS Gateways",
  email: "Email Systems",
};

// ── Helpers ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: IntegrationStatus }) {
  const map: Record<IntegrationStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
    connected: { label: "Connected", variant: "default", icon: <CheckCircle2 className="h-3 w-3" /> },
    disconnected: { label: "Disconnected", variant: "secondary", icon: <XCircle className="h-3 w-3" /> },
    error: { label: "Error", variant: "destructive", icon: <XCircle className="h-3 w-3" /> },
    pending: { label: "Pending", variant: "outline", icon: <Clock className="h-3 w-3" /> },
  };
  const { label, variant, icon } = map[status];
  return <Badge variant={variant} className="gap-1">{icon}{label}</Badge>;
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    POST: "bg-blue-500/10 text-blue-700 dark:text-blue-400",
    PUT: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    DELETE: "bg-red-500/10 text-red-700 dark:text-red-400",
  };
  return <span className={`px-2 py-0.5 rounded text-xs font-mono font-bold ${colors[method] || ""}`}>{method}</span>;
}

// ── Main Component ──────────────────────────────────────────────────────

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>(INITIAL_INTEGRATIONS);
  const [configDialog, setConfigDialog] = useState<Integration | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>("all");

  const apiKey = "ak_live_c8f2e91d4a7b3f5e6d8c0a1b2e4f6a8d";
  const [showRestKey, setShowRestKey] = useState(false);

  const stats = {
    total: integrations.length,
    connected: integrations.filter(i => i.status === "connected").length,
    pending: integrations.filter(i => i.status === "pending").length,
    errors: integrations.filter(i => i.status === "error").length,
  };

  const toggleConnection = (id: string) => {
    setIntegrations(prev => prev.map(i => {
      if (i.id !== id) return i;
      if (i.status === "connected") {
        toast.success(`${i.name} disconnected`);
        return { ...i, status: "disconnected" as IntegrationStatus, lastSync: undefined, apiKeyMasked: undefined };
      }
      toast.success(`${i.name} connected successfully`);
      return { ...i, status: "connected" as IntegrationStatus, lastSync: new Date().toISOString(), apiKeyMasked: `${i.id.slice(0, 4)}_****live` };
    }));
  };

  const syncIntegration = (id: string) => {
    setIntegrations(prev => prev.map(i =>
      i.id === id ? { ...i, lastSync: new Date().toISOString() } : i
    ));
    toast.success("Sync completed");
  };

  const filtered = filterCategory === "all" ? integrations : integrations.filter(i => i.category === filterCategory);
  const grouped = Object.entries(CATEGORY_LABELS).map(([cat, label]) => ({
    category: cat,
    label,
    items: filtered.filter(i => i.category === cat),
  })).filter(g => g.items.length > 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <Plug className="h-5 w-5 sm:h-6 sm:w-6 text-primary" /> API Integrations
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Connect payment gateways, banks, GST portals, and communication services</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total", value: stats.total, icon: <Plug className="h-4 w-4" />, color: "text-primary" },
          { label: "Connected", value: stats.connected, icon: <CheckCircle2 className="h-4 w-4" />, color: "text-emerald-600" },
          { label: "Pending", value: stats.pending, icon: <Clock className="h-4 w-4" />, color: "text-amber-600" },
          { label: "Errors", value: stats.errors, icon: <XCircle className="h-4 w-4" />, color: "text-destructive" },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className={s.color}>{s.icon}</div>
              <div>
                <p className="text-lg sm:text-2xl font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="integrations" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto scrollable-tabs">
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="rest-api">REST API</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="logs">API Logs</TabsTrigger>
        </TabsList>

        {/* ── Integrations Tab ── */}
        <TabsContent value="integrations" className="space-y-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Button size="sm" variant={filterCategory === "all" ? "default" : "outline"} onClick={() => setFilterCategory("all")}>All</Button>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <Button key={k} size="sm" variant={filterCategory === k ? "default" : "outline"} onClick={() => setFilterCategory(k)} className="whitespace-nowrap">{v}</Button>
            ))}
          </div>

          {grouped.map(group => (
            <div key={group.category} className="space-y-3">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{group.label}</h3>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {group.items.map(integration => (
                  <Card key={integration.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-muted">{integration.icon}</div>
                          <div>
                            <p className="font-semibold text-foreground text-sm">{integration.name}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{integration.description}</p>
                          </div>
                        </div>
                        <StatusBadge status={integration.status} />
                      </div>

                      {integration.status === "connected" && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          {integration.apiKeyMasked && <p>Key: <code className="bg-muted px-1 rounded">{integration.apiKeyMasked}</code></p>}
                          {integration.lastSync && <p>Last sync: {new Date(integration.lastSync).toLocaleString("en-IN")}</p>}
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-[10px]">{integration.environment}</Badge>
                            {integration.version && <Badge variant="outline" className="text-[10px]">{integration.version}</Badge>}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 pt-1">
                        <Switch checked={integration.status === "connected"} onCheckedChange={() => toggleConnection(integration.id)} />
                        <span className="text-xs text-muted-foreground">{integration.status === "connected" ? "Enabled" : "Disabled"}</span>
                        {integration.status === "connected" && (
                          <>
                            <Button size="sm" variant="ghost" className="ml-auto h-7 px-2" onClick={() => syncIntegration(integration.id)}>
                              <RefreshCw className="h-3 w-3 mr-1" /> Sync
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setConfigDialog(integration)}>
                              <Settings className="h-3 w-3" />
                            </Button>
                          </>
                        )}
                        {integration.status === "disconnected" && (
                          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => setConfigDialog(integration)}>
                            Configure
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </TabsContent>

        {/* ── REST API Tab ── */}
        <TabsContent value="rest-api" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Key className="h-4 w-4" /> API Authentication</CardTitle>
              <CardDescription>Use your API key to authenticate third-party requests</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input readOnly value={showRestKey ? apiKey : "ak_live_••••••••••••••••••••••••••••••••"} className="pr-10 font-mono text-sm" />
                    <Button size="sm" variant="ghost" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0" onClick={() => setShowRestKey(!showRestKey)}>
                      {showRestKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(apiKey); toast.success("API key copied"); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Base URL</Label>
                <div className="flex gap-2">
                  <Input readOnly value="https://api.accoupro.in/v1" className="font-mono text-sm" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText("https://api.accoupro.in/v1"); toast.success("URL copied"); }}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs font-mono text-muted-foreground">
                  curl -H "Authorization: Bearer {'<'}API_KEY{'>'}" \<br />
                  &nbsp;&nbsp;https://api.accoupro.in/v1/ledgers
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Code className="h-4 w-4" /> API Endpoints</CardTitle>
              <CardDescription>Available REST endpoints for third-party integration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {REST_API_ENDPOINTS.map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-muted/50 transition-colors text-sm">
                    <MethodBadge method={ep.method} />
                    <code className="font-mono text-xs text-foreground flex-1 min-w-0 truncate">{ep.path}</code>
                    <span className="hidden sm:block text-xs text-muted-foreground truncate max-w-[200px]">{ep.description}</span>
                    <Badge variant="outline" className="text-[10px] hidden md:inline-flex">{ep.auth}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Webhooks Tab ── */}
        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4" /> Webhook Endpoints</CardTitle>
              <CardDescription>Receive real-time notifications when events occur</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { event: "voucher.created", url: "https://erp.example.com/hooks/voucher", status: "active", lastTriggered: "2026-03-08T10:12:00Z" },
                { event: "invoice.paid", url: "https://crm.example.com/hooks/invoice", status: "active", lastTriggered: "2026-03-07T16:30:00Z" },
                { event: "gst.filed", url: "https://compliance.example.com/hooks/gst", status: "paused", lastTriggered: undefined },
              ].map((wh, i) => (
                <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm font-semibold text-foreground">{wh.event}</p>
                    <p className="text-xs text-muted-foreground truncate">{wh.url}</p>
                    {wh.lastTriggered && <p className="text-xs text-muted-foreground">Last: {new Date(wh.lastTriggered).toLocaleString("en-IN")}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={wh.status === "active" ? "default" : "secondary"}>{wh.status}</Badge>
                    <Button size="sm" variant="ghost" className="h-7"><Settings className="h-3 w-3" /></Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" className="w-full sm:w-auto">
                <Globe className="h-4 w-4 mr-2" /> Add Webhook
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Available Events</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {["voucher.created", "voucher.approved", "voucher.rejected", "invoice.created", "invoice.paid", "invoice.overdue", "gst.filed", "gst.error", "eway.generated", "eway.expired", "payment.received", "payment.failed", "reconciliation.complete", "backup.complete"].map(e => (
                  <div key={e} className="flex items-center gap-2 p-2 rounded bg-muted/50">
                    <Zap className="h-3 w-3 text-primary" />
                    <code className="text-xs font-mono">{e}</code>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── API Logs Tab ── */}
        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" /> Recent API Activity</CardTitle>
              <CardDescription>Last 24 hours of API calls across all integrations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {[
                  { time: "11:02 AM", method: "POST", path: "/v1/vouchers", status: 201, duration: "142ms", source: "Razorpay Webhook" },
                  { time: "10:45 AM", method: "GET", path: "/v1/ledgers", status: 200, duration: "89ms", source: "ERP Sync" },
                  { time: "10:30 AM", method: "POST", path: "/v1/invoices", status: 201, duration: "256ms", source: "REST API" },
                  { time: "10:15 AM", method: "GET", path: "/v1/gst/returns", status: 200, duration: "312ms", source: "GST Portal" },
                  { time: "09:45 AM", method: "POST", path: "/v1/eway-bills", status: 201, duration: "189ms", source: "E-Way NIC" },
                  { time: "09:30 AM", method: "GET", path: "/v1/reports/trial-balance", status: 200, duration: "445ms", source: "REST API" },
                  { time: "08:15 AM", method: "POST", path: "/v1/webhooks", status: 401, duration: "12ms", source: "Unknown" },
                  { time: "06:00 AM", method: "GET", path: "/v1/reconciliation", status: 200, duration: "678ms", source: "ICICI Bank API" },
                ].map((log, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors text-sm">
                    <span className="text-xs text-muted-foreground w-16 shrink-0">{log.time}</span>
                    <MethodBadge method={log.method} />
                    <code className="font-mono text-xs truncate flex-1 min-w-0">{log.path}</code>
                    <Badge variant={log.status < 400 ? "default" : "destructive"} className="text-[10px]">{log.status}</Badge>
                    <span className="text-xs text-muted-foreground hidden sm:block w-12 text-right">{log.duration}</span>
                    <span className="text-xs text-muted-foreground hidden md:block truncate max-w-[120px]">{log.source}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Config Dialog ── */}
      <Dialog open={!!configDialog} onOpenChange={() => { setConfigDialog(null); setShowApiKey(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {configDialog?.icon} Configure {configDialog?.name}
            </DialogTitle>
            <DialogDescription>{configDialog?.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>API Key / Secret</Label>
              <div className="relative">
                <Input type={showApiKey ? "text" : "password"} placeholder="Enter API key..." defaultValue={configDialog?.apiKeyMasked || ""} />
                <Button size="sm" variant="ghost" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0" onClick={() => setShowApiKey(!showApiKey)}>
                  {showApiKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Environment</Label>
              <Select defaultValue={configDialog?.environment || "sandbox"}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox / Test</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {configDialog?.category === "payment" && (
              <div className="space-y-2">
                <Label>Webhook URL</Label>
                <Input readOnly value={`https://api.accoupro.in/webhooks/${configDialog.id}`} className="font-mono text-xs" />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setConfigDialog(null)}>Cancel</Button>
            <Button onClick={() => { toggleConnection(configDialog!.id); setConfigDialog(null); }}>
              {configDialog?.status === "connected" ? "Update" : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
